import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import PageWrapper from "@/components/layout/PageWrapper";
import ContactForm from "@/components/feature/ContactForm";

export default function Contact() {
  const { t } = useTranslation();
  const phoneDisplay = t("contact.phoneNumber");
  const phoneHref = `tel:${phoneDisplay.replace(/[^+\d]/g, "")}`;

  return (
    <PageWrapper>
      {/* SEO */}
      <title>{t("meta.contactTitle")}</title>
      <meta name="description" content={t("meta.contactDesc")} />

      {/* Hero */}
      <section className="relative w-full min-h-[350px] md:min-h-[400px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            src="/assets/images/20.jpg"
            alt="Contact Arafouf"
            className="w-full h-full object-cover object-top"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.4, ease: "easeOut" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-black/20" />
        </div>
        <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 pb-12 md:pb-20 pt-32 md:pt-40">
          <motion.h1
            className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {t("contact.pageTitle")}
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-white/80 max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t("contact.pageDesc")}
          </motion.p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="w-full py-16 md:py-24 bg-[#F5F3EF]">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Form */}
            <motion.div
              className="bg-white rounded-2xl p-8 md:p-10 border border-stone-200 shadow-[0_14px_40px_rgba(0,0,0,0.04)]"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              whileHover={{ y: -4 }}
            >
              <ContactForm title={t("contact.formTitle")} />
            </motion.div>

            {/* Info */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-stone-200 shadow-[0_14px_40px_rgba(0,0,0,0.04)]">
                <motion.div className="flex items-start gap-4 mb-6" whileHover={{ scale: 1.01 }}>
                  <span className="w-10 h-10 flex items-center justify-center bg-brand/10 text-brand rounded-lg flex-shrink-0">
                    <i className="ri-phone-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm text-stone-500 mb-1">{t("contact.phoneLabel")}</p>
                    <a
                      href={phoneHref}
                      dir="ltr"
                      className="text-lg font-semibold text-stone-900 hover:text-brand transition-colors cursor-pointer"
                      style={{ unicodeBidi: "isolate" }}
                    >
                      {phoneDisplay}
                    </a>
                  </div>
                </motion.div>

                <motion.div className="flex items-start gap-4 mb-6" whileHover={{ scale: 1.01 }}>
                  <span className="w-10 h-10 flex items-center justify-center bg-brand/10 text-brand rounded-lg flex-shrink-0">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm text-stone-500 mb-1">{t("contact.emailLabel")}</p>
                    <a
                      href={`mailto:${t("contact.emailAddress")}`}
                      className="text-lg font-semibold text-stone-900 hover:text-brand transition-colors cursor-pointer"
                    >
                      {t("contact.emailAddress")}
                    </a>
                  </div>
                </motion.div>

                <motion.div className="flex items-start gap-4 mb-6" whileHover={{ scale: 1.01 }}>
                  <span className="w-10 h-10 flex items-center justify-center bg-brand/10 text-brand rounded-lg flex-shrink-0">
                    <i className="ri-map-pin-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm text-stone-500 mb-1">{t("contact.locationLabel")}</p>
                    <p className="text-lg font-semibold text-stone-900">{t("contact.address1")}</p>
                    <p className="text-base text-stone-600">{t("contact.address2")}</p>
                  </div>
                </motion.div>

                <motion.div className="flex items-start gap-4" whileHover={{ scale: 1.01 }}>
                  <span className="w-10 h-10 flex items-center justify-center bg-brand/10 text-brand rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-lg" />
                  </span>
                  <div>
                    <p className="text-sm text-stone-500 mb-1">{t("contact.hoursLabel")}</p>
                    <p className="text-lg font-semibold text-stone-900">{t("contact.hoursValue")}</p>
                  </div>
                </motion.div>
              </div>

              {/* Map */}
              <motion.div
                className="bg-white rounded-2xl overflow-hidden border border-stone-200 shadow-[0_14px_40px_rgba(0,0,0,0.04)]"
                whileHover={{ y: -4 }}
                transition={{ duration: 0.25 }}
              >
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3626.034935371727!2d46.80871319999999!3d24.656925800000003!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e2f0700ebce8719%3A0x23e14f59a1195c91!2z2KPYqNix2KfYrCDYp9mE2LHZgdmI2YE!5e0!3m2!1sen!2ssa!4v1778348269633!5m2!1sen!2ssa"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Arafouf Location"
                  className="rounded-xl"
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageWrapper>
  );
}